import React, { useState, useEffect } from 'react';
import useResponsive from '../hooks/useResponsive';
import useModalSize from '../hooks/useModalSize';
import { STATUS_MAP, CATEGORY_MAP, getTwoFaUrl, setTwoFaUrlCache } from '../constants/mappings';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Row,
  Col,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Descriptions,
  Divider,
  Pagination,
  Image,
  List,
  Form,
  Spin,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  ImportOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  CopyOutlined,
  SettingOutlined,
  SwapOutlined
} from '@ant-design/icons';
// 移除路由相关导入
import { getAccounts, deleteAccount, batchUpdateAccountStatus, batchDeleteAccounts, exportAccounts, importAccounts, getCategories, getAccountTypes, createAccountType, updateAccountType, deleteAccountType, getTwoFaSettings } from '../services/api';
import AccountForm from './AccountForm';
import SaleForm from './SaleForm';
import AccountTypeManager from './AccountTypeManager';
import { getAccountImages } from '../services/api';

const { Search } = Input;
const { Option } = Select;

const AccountList = ({ onNavigate, onDataChanged, mode = 'normal' }) => {
  const isWhitePool = mode === 'white';
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isMobile } = useResponsive();
  const { modalWidth } = useModalSize();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    window_status: '',
    country: '',
    minFollowers: undefined,
    maxFollowers: undefined
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [saleModalVisible, setSaleModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedAccountForSale, setSelectedAccountForSale] = useState(null);
  const [viewingAccount, setViewingAccount] = useState(null);
  const [viewingImages, setViewingImages] = useState([]);
  const [formSeed, setFormSeed] = useState(0);
  const [accountTypeModalVisible, setAccountTypeModalVisible] = useState(false);
  const [accountTypes, setAccountTypes] = useState([]);
  const [importVisible, setImportVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [displayImages, setDisplayImages] = useState([]);

  // 将 base64 大图压缩为较小显示图（只处理 dataURL，普通 URL 直接返回）
  async function shrinkDataUrl(src, maxSize = 800, quality = 0.75) {
    // 普通 URL：不缩以避免跨域 Canvas 污染
    if (!/^data:image\//.test(src)) return src;
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
            const w = Math.max(1, Math.round(img.width * ratio));
            const h = Math.max(1, Math.round(img.height * ratio));
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch (e) {
            resolve(src);
          }
        };
        img.onerror = () => resolve(src);
        img.src = src;
      } catch (_) {
        resolve(src);
      }
    });
  }

  useEffect(() => {
    fetchAccounts();
  }, [pagination.current, pagination.pageSize, filters, mode]);

  // 切换账号列表 / 白号库存时清空旧数据和筛选，避免串数据
  useEffect(() => {
    setAccounts([]);
    setSelectedRowKeys([]);
    setFilters({
      search: '',
      status: '',
      category: '',
      window_status: '',
      country: '',
      minFollowers: undefined,
      maxFollowers: undefined
    });
    setPagination(prev => ({ ...prev, current: 1, total: 0 }));
  }, [mode]);

  useEffect(() => {
    fetchCategories();
    fetchAccountTypes();
    getTwoFaSettings()
      .then((data) => {
        if (data?.two_fa_url) setTwoFaUrlCache(data.two_fa_url);
      })
      .catch(() => {});
  }, []);

  // 响应式逻辑由 useResponsive 提供

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        pool: isWhitePool ? 'white' : 'normal'
      };
      // 白号库存：强制 status=white（兼容旧 Worker 不认 pool 的情况）
      if (isWhitePool) {
        params.status = 'white';
      }
      const response = await getAccounts(params);
      setAccounts(response.accounts);
      setPagination(prev => ({
        ...prev,
        total: response.total
      }));
    } catch (error) {
      message.error('获取账号列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categories = await getCategories();
      setAvailableCategories(Array.isArray(categories) ? categories : []);
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const types = await getAccountTypes();
      setAccountTypes(types);
    } catch (error) {
      console.error('获取账号类型失败:', error);
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleCategoryFilter = (value) => {
    setFilters(prev => ({ ...prev, category: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDelete = async (id) => {
    try {
      await deleteAccount(id);
      message.success('删除成功');
      fetchAccounts();
      onDataChanged && onDataChanged();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleBatchStatusUpdate = async (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要更新的账号');
      return;
    }
    try {
      const result = await batchUpdateAccountStatus(selectedRowKeys, status);
      const ok = (result && typeof result.success === 'number') ? result.success : selectedRowKeys.length;
      const total = (result && typeof result.total === 'number') ? result.total : selectedRowKeys.length;
      message.success(`成功更新 ${ok}/${total} 个账号状态`);
      setAccounts(prev => prev.map(a => (
        selectedRowKeys.includes(a.id) ? { ...a, status } : a
      )));
      setSelectedRowKeys([]);
      fetchAccounts();
      onDataChanged && onDataChanged();
    } catch (error) {
      message.error('批量更新失败');
    }
  };

  const transferLabel = (status) => (status === 'pending' ? '待售' : status === 'banned' ? '起号' : status);

  const handleTransferIn = (ids, status) => {
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!list.length) {
      message.warning('请选择要转入的白号');
      return;
    }
    Modal.confirm({
      title: '转入账号管理',
      content: `将 ${list.length} 个白号转为「${transferLabel(status)}」？转入后可在账号列表最前查看，创建时间会更新为转入时间。`,
      okText: '确认转入',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await batchUpdateAccountStatus(list, status, { transfer: true });
          const ok = (result && typeof result.success === 'number') ? result.success : list.length;
          message.success(`已转入账号管理：${ok} 条（${transferLabel(status)}），已排到列表最前`);
          setSelectedRowKeys([]);
          fetchAccounts();
          onDataChanged && onDataChanged();
        } catch (e) {
          message.error(e?.message || '转入失败');
        }
      }
    });
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的账号');
      return;
    }
    Modal.confirm({
      title: '批量删除',
      content: `确定删除选中的 ${selectedRowKeys.length} 个账号吗？此操作不可撤销。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await batchDeleteAccounts(selectedRowKeys);
          message.success(`删除完成：成功 ${res.success} 个，失败 ${selectedRowKeys.length - res.success} 个`);
          setSelectedRowKeys([]);
          fetchAccounts();
        } catch (e) {
          message.error('批量删除失败');
        }
      }
    });
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormSeed(Date.now());
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingAccount(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchAccounts();
    fetchCategories(); // 刷新分类列表
  };

  const handleSaleModalClose = () => {
    setSaleModalVisible(false);
    setSelectedAccountForSale(null);
  };

  const handleSaleModalSuccess = (payload) => {
    // 乐观更新：将当前选中账号状态置为已售，并同步价格/利润
    if (selectedAccountForSale && selectedAccountForSale.id) {
      const price = Number(payload?.price ?? payload?.salePrice ?? selectedAccountForSale.price);
      const profit = Number(payload?.profit ?? selectedAccountForSale.profit);
      setAccounts(prev => prev.map(a => (
        a.id === selectedAccountForSale.id ? { ...a, status: 'sold', price, profit } : a
      )));
    }

    handleSaleModalClose();
    fetchAccounts();
    // 通知上层刷新仪表盘
    onDataChanged && onDataChanged();
    // 触发一次 stats 刷新（若父级未监听）
    try {
      if (window && window.dispatchEvent) {
        window.dispatchEvent(new Event('erp:stats:refresh'));
      }
    } catch (_) {}
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setViewingAccount(null);
    setViewingImages([]);
  };

  const handleNavigate = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportAccounts();
      const blob = new Blob([res], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tiktok-accounts-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功（CSV，可用Excel打开）');
    } catch (error) {
      message.error('导出失败');
    }
  };

  // 导出为 .xlsx 并设置列宽（单位为字符数）
  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // 直接从 API 获取所有账号数据（JSON格式），而不是解析 CSV
      const response = await getAccounts({
        page: 1,
        limit: 10000,
        forExport: 1,
        pool: isWhitePool ? 'white' : 'normal'
      });
      const allAccounts = response.accounts || [];
      
      const XLSX = await import('xlsx');

      // 定义中文表头
      const headers = [
        '用户名', '密码', '邮箱', '邮箱密码', '2FA', '手机号', '粉丝', '国家', '地区占比',
        '分类', '状态', 'API', '售价', '成本', '利润', '橱窗状态', '开通橱窗日期',
        '描述', '创建时间', '最后更新时间'
      ];

      // 状态映射
      const statusMap = {
        'pending': '待售',
        'suspended': '封禁',
        'banned': '起号',
        'sold': '已售',
        'white': '白号'
      };

      // 处理图片 URLs
      const processImageUrls = (account) => {
        let imgs = [];
        if (account.image_urls) {
          try {
            imgs = Array.isArray(account.image_urls)
              ? account.image_urls
              : JSON.parse(account.image_urls);
          } catch (e) {
            imgs = typeof account.image_urls === 'string'
              ? account.image_urls.split(',').map(s => s.trim()).filter(Boolean)
              : [];
          }
        }
        if (account.image_url && !imgs.includes(account.image_url)) {
          imgs.unshift(account.image_url);
        }
        return imgs;
      };

      // 转换数据为数组格式，所有值转为字符串并强制文本格式
      const data = allAccounts.map(acc => {
        const images = processImageUrls(acc);
        const row = [
          String(acc.username || ''),
          String(acc.password || ''),
          String(acc.email || ''),
          String(acc.emailPassword || ''),
          String(acc.two_fa || ''),
          String(acc.phone || ''),
          String(acc.followers || 0),
          String(acc.country || ''),
          String(acc.region_ratio || ''),
          String(acc.category || ''),
          String(statusMap[acc.status] || acc.status || ''),
          String(acc.api_key || ''),
          String(acc.price || 0),
          String(acc.cost || 0),
          String(acc.profit || 0),
          String(acc.window_status || '未开'),
          acc.window_opened_at ? new Date(acc.window_opened_at).toLocaleDateString('zh-CN') : '',
          String(acc.description || ''),
          acc.created_date ? new Date(acc.created_date).toLocaleString('zh-CN') : '',
          acc.updated_at || acc.last_checked ? new Date(acc.updated_at || acc.last_checked).toLocaleString('zh-CN') : ''
        ];
        
        // 添加图片列（最多显示10张）
        for (let i = 0; i < 10; i++) {
          row.push(String(images[i] || ''));
        }
        
        return row;
      });

      // 更新表头，添加图片列
      const finalHeaders = [...headers];
      for (let i = 1; i <= 10; i++) {
        finalHeaders.push(`图片${i}`);
      }

      // 创建工作表
      const ws = XLSX.utils.aoa_to_sheet([finalHeaders, ...data]);

      // 强制所有单元格为文本格式，防止 Excel 自动转换数据类型
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let row = 0; row <= range.e.r; row++) {
        for (let col = 0; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          
          // 确保所有单元格都是文本格式
          ws[cellAddress].t = 's'; // 字符串类型
          ws[cellAddress].z = '@'; // 文本格式（Excel数字格式代码 @ 表示文本）
          
          // 如果值看起来像数字或日期，在前面加单引号强制为文本
          const value = ws[cellAddress].v;
          if (value && typeof value === 'string') {
            // 如果包含 @ 符号（邮箱）或看起来像日期时间，确保是文本
            if (value.includes('@') || /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(value)) {
              ws[cellAddress].v = value; // 保持原值，但格式已设置为文本
            }
          }
        }
      }

      // 设置列宽
      const cols = [
        { wch: 21 }, // 用户名
        { wch: 21 }, // 密码
        { wch: 30 }, // 邮箱
        { wch: 26 }, // 邮箱密码
        { wch: 18 }, // 手机号
        { wch: 8 },  // 粉丝
        { wch: 8 },  // 国家
        { wch: 15 }, // 地区占比
        { wch: 12 }, // 分类
        { wch: 8 },  // 状态
        { wch: 30 }, // API
        { wch: 10 }, // 售价
        { wch: 10 }, // 成本
        { wch: 10 }, // 利润
        { wch: 12 }, // 橱窗状态
        { wch: 15 }, // 开通橱窗日期
        { wch: 30 }, // 描述
        { wch: 20 }, // 创建时间
        { wch: 20 }, // 最后更新时间
      ];
      
      // 图片列宽度
      for (let i = 0; i < 10; i++) {
        cols.push({ wch: 50 });
      }
      
      ws['!cols'] = cols;

      // 创建工作簿并写入文件
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '账号列表');
      
      const filename = `tiktok-accounts-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      message.success(`导出成功：共 ${allAccounts.length} 条记录（Excel，所有列强制为文本格式）`);
    } catch (error) {
      console.error('导出Excel失败:', error);
      message.error('导出Excel失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const showImportResult = (res) => {
    const success = Number(res?.success || 0);
    const failed = Number(res?.failed || 0);
    const skipped = Number(res?.skipped || 0);
    const total = Number(res?.total || success + failed + skipped);
    const text = `导入完成：成功 ${success} 条，失败 ${failed} 条，跳过已存在 ${skipped} 条${total ? `（共 ${total} 条）` : ''}`;
    if (success === 0 && failed === 0 && skipped > 0) {
      message.warning(text + '。账号用户名或邮箱已在系统中，不会重复导入。');
    } else if (success === 0 && failed > 0 && skipped === 0) {
      message.error(text + '。请确认表头含「用户名」或「邮箱」列。');
    } else if (success === 0 && failed === 0 && skipped === 0) {
      message.warning('未导入任何数据，请检查文件内容和表头。');
    } else {
      message.success(text);
    }
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await importAccounts(form, isWhitePool ? { defaultStatus: 'white' } : {});
      showImportResult(res);
      setImportVisible(false);
      fetchAccounts();
      onDataChanged && onDataChanged();
    } catch (e) {
      message.error(e?.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const downloadImportTemplate = () => {
    const header = '用户名,密码,邮箱,邮箱密码,2FA,橱窗状态,粉丝,地区,类型,价格\n';
    const blob = new Blob(['\ufeff' + header], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '账号批量导入模板.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // 解析 Excel（.xlsx）为数组→再转为后端可接受的 JSON 数组
  const handleImportExcel = async (file) => {
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (!rows || rows.length === 0) {
        message.warning('Excel 中没有数据');
        return;
      }
      const res = await importAccounts(rows, isWhitePool ? { defaultStatus: 'white' } : {});
      showImportResult(res);
      setImportVisible(false);
      fetchAccounts();
      onDataChanged && onDataChanged();
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'Excel 导入失败');
    } finally {
      setImporting(false);
    }
  };

  const getStatusTag = (status) => {
    const config = STATUS_MAP[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getCategoryTag = (category) => {
    const config = CATEGORY_MAP[category] || { color: 'default', text: category };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleCopyInfo = async (record) => {
    try {
      const parts = [];
      if (record.username) parts.push(`用户名: ${record.username}`);
      if (record.password) parts.push(`密码: ${record.password}`);
      if (record.email) parts.push(`邮箱: ${record.email}`);
      if (record.emailPassword) parts.push(`邮箱密码: ${record.emailPassword}`);
      if (record.api_key) parts.push(`API: ${record.api_key}`);
      const twoFa = typeof record.two_fa === 'string' ? record.two_fa.trim() : '';
      if (twoFa) {
        parts.push(`2FA: ${twoFa}`);
        parts.push(`2FA接码: ${getTwoFaUrl()}`);
      }
      const text = parts.join('\n');
      if (!text) {
        message.warning('无可复制的信息');
        return;
      }
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success('关键信息已复制');
    } catch (e) {
      message.error('复制失败');
    }
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          {record.email && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
          )}
          {record.country && (
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>地区：{record.country}</div>
          )}
          {(() => {
            let imgs = [];
            if (record.image_urls) {
              try {
                imgs = Array.isArray(record.image_urls)
                  ? record.image_urls
                  : JSON.parse(record.image_urls);
              } catch (e) {
                imgs = String(record.image_urls)
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean);
              }
            }
            if (record.image_url && !imgs.includes(record.image_url)) {
              imgs.unshift(record.image_url);
            }
            return imgs.length > 0 ? (
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <Image.PreviewGroup>
                  {imgs.map((u, idx) => (
                    <Image
                      key={`${u}-${idx}`}
                      src={u}
                      width={36}
                      height={36}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      preview={{ mask: '点击预览' }}
                    />
                  ))}
                </Image.PreviewGroup>
              </div>
            ) : null;
          })()}
        </div>
      )
    },
    {
      title: '橱窗状态',
      dataIndex: 'window_status',
      key: 'window_status',
      render: (value) => {
        const color = value === '已开' ? 'green' : value === '掉窗' ? 'red' : 'default';
        return <Tag color={color}>{value || '未开'}</Tag>;
      }
    },
    {
      title: '粉丝数/占比',
      dataIndex: 'followers',
      key: 'followers',
      render: (value, record) => (
        <span>
          {value ? Number(value).toLocaleString() : '0'}
          {` / ${record.region_ratio || '-'}`}
        </span>
      ),
      sorter: (a, b) => a.followers - b.followers
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (category) => getCategoryTag(category)
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status)
    },
    {
      title: 'API',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (apiKey) => {
        if (!apiKey) return <span style={{ fontSize: 12, color: '#666' }}>-</span>;
        const url = String(apiKey).trim();
        if (/^https?:\/\//i.test(url)) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 12 }}
              title={url}
            >
              打开
            </a>
          );
        }
        return (
          <span style={{ fontSize: 12, color: '#666' }} title={url}>
            {url.substring(0, 8)}...
          </span>
        );
      }
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      render: (value) => `¥${value}`,
      sorter: (a, b) => a.price - b.price
    },
    {
      title: '利润',
      dataIndex: 'profit',
      key: 'profit',
      render: (value) => (
        <span style={{ color: value > 0 ? '#52c41a' : '#f5222d' }}>
          ¥{value}
        </span>
      ),
      sorter: (a, b) => a.profit - b.profit
    },
    {
      title: '创建时间',
      dataIndex: 'created_date',
      key: 'created_date',
      render: (date) => (date ? new Date(date).toLocaleDateString('zh-CN') : '-'),
      sorter: (a, b) => new Date(a.created_date) - new Date(b.created_date)
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={async () => {
                setViewingAccount(record);
                setDetailModalVisible(true);
                setImagesLoading(true);
                setViewingImages([]);
                try {
                  const res = await getAccountImages(record.id);
                  const raws = Array.isArray(res.image_urls) ? res.image_urls : [];
                  setViewingImages(raws);
                } catch (e) {
                  setViewingImages([]);
                } finally {
                  setImagesLoading(false);
                }
              }}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="复制关键信息">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCopyInfo(record)}
            />
          </Tooltip>
          {isWhitePool ? (
            <Dropdown
              menu={{
                items: [
                  { key: 'pending', label: '转入待售', onClick: () => handleTransferIn([record.id], 'pending') },
                  { key: 'banned', label: '转入起号', onClick: () => handleTransferIn([record.id], 'banned') }
                ]
              }}
            >
              <Tooltip title="转入账号管理">
                <Button type="text" icon={<SwapOutlined />} />
              </Tooltip>
            </Dropdown>
          ) : (
            record.status !== 'sold' && (
              <Tooltip title="添加销售">
                <Button
                  type="text"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => {
                    setSelectedAccountForSale(record);
                    setSaleModalVisible(true);
                  }}
                />
              </Tooltip>
            )
          )}
          <Popconfirm
            title="确定要删除这个账号吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.status === 'sold'
    })
  };

  return (
    <div>
      <Card title={isWhitePool ? '白号库存' : '账号列表'} extra={isWhitePool ? (
        <span style={{ color: '#8c8c8c', fontSize: 13 }}>登录前凭证池 · 登录后可转入账号管理</span>
      ) : null}>
        <div style={{ marginBottom: '16px' }}>
          {isMobile ? (
            <div>
              <div style={{ marginBottom: 12 }}>
                <Search
                  placeholder="搜索用户名、邮箱或标签"
                  style={{ width: '100%' }}
                  onSearch={handleSearch}
                  enterButton={<SearchOutlined />}
                />
              </div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={8}>
                  <Select
                    placeholder="状态筛选"
                    style={{ width: '100%' }}
                    allowClear
                    onChange={handleStatusFilter}
                  >
                    <Option value="banned">起号</Option>
                    <Option value="suspended">封禁</Option>
                    <Option value="pending">待售</Option>
                    <Option value="sold">已售</Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <Select
                    placeholder="分类筛选"
                    style={{ width: '100%' }}
                    allowClear
                    onChange={handleCategoryFilter}
                  >
                    {availableCategories.map(category => (
                      <Option key={category} value={category}>
                        {category}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Select
                    placeholder="地区筛选"
                    style={{ width: '100%' }}
                    allowClear
                    onChange={(value) => {
                      setFilters(prev => ({ ...prev, country: value }));
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                  >
                    {['美国','英国','加拿大','澳大利亚','德国','法国','日本','韩国','新加坡','中国','其他'].map(c => (
                      <Option key={c} value={c}>{c}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Select
                    placeholder="橱窗状态"
                    style={{ width: '100%' }}
                    allowClear
                    onChange={(value) => {
                      setFilters(prev => ({ ...prev, window_status: value }));
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                  >
                    <Option value="已开">已开</Option>
                    <Option value="未开">未开</Option>
                    <Option value="掉窗">掉窗</Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="粉丝数最小"
                    type="number"
                    onChange={(e) => {
                      const v = e?.target?.value;
                      setFilters(prev => ({ ...prev, minFollowers: v ? Number(v) : undefined }));
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="粉丝数最大"
                    type="number"
                    onChange={(e) => {
                      const v = e?.target?.value;
                      setFilters(prev => ({ ...prev, maxFollowers: v ? Number(v) : undefined }));
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                  />
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={6}>
                  <Button
                    block
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingAccount(null);
                      setFormSeed(Date.now());
                      setModalVisible(true);
                    }}
                  >
                    添加
                  </Button>
                </Col>
                <Col span={6}>
                  <Button 
                    block 
                    size="small" 
                    icon={<SettingOutlined />} 
                    onClick={() => setAccountTypeModalVisible(true)}
                  >
                    类型
                  </Button>
                </Col>
                <Col span={6}>
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'excel', label: '导出Excel', onClick: handleExportExcel },
                        { key: 'csv', label: '导出CSV', onClick: handleExport }
                      ]
                    }}
                  >
                    <Button block size="small" icon={<ExportOutlined />}>导出</Button>
                  </Dropdown>
                </Col>
                <Col span={6}>
                  <Button block size="small" icon={<ReloadOutlined />} onClick={fetchAccounts}>刷新</Button>
                </Col>
              </Row>
            </div>
          ) : (
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Space wrap>
                  <Search
                    placeholder="搜索用户名、邮箱或标签"
                    style={{ width: 300 }}
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                  />
                  <Select placeholder="状态筛选" style={{ width: 120 }} allowClear onChange={handleStatusFilter}>
                    <Option value="banned">起号</Option>
                    <Option value="suspended">封禁</Option>
                    <Option value="pending">待售</Option>
                    <Option value="sold">已售</Option>
                  </Select>
                  <Select placeholder="分类筛选" style={{ width: 120 }} allowClear onChange={handleCategoryFilter}>
                    {availableCategories.map(category => (
                      <Option key={category} value={category}>{category}</Option>
                    ))}
                  </Select>
                  <Select placeholder="地区筛选" style={{ width: 120 }} allowClear onChange={(value) => {
                    setFilters(prev => ({ ...prev, country: value }));
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}>
                    {['美国','英国','加拿大','澳大利亚','德国','法国','日本','韩国','新加坡','中国','其他'].map(c => (
                      <Option key={c} value={c}>{c}</Option>
                    ))}
                  </Select>
                  <Select placeholder="橱窗状态" style={{ width: 120 }} allowClear onChange={(value) => {
                    setFilters(prev => ({ ...prev, window_status: value }));
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}>
                    <Option value="已开">已开</Option>
                    <Option value="未开">未开</Option>
                    <Option value="掉窗">掉窗</Option>
                  </Select>
                  <Input placeholder="粉丝数最小" type="number" style={{ width: 120 }} onChange={(e)=>{
                    const v = e?.target?.value;
                    setFilters(prev=>({ ...prev, minFollowers: v ? Number(v) : undefined }));
                    setPagination(prev=>({ ...prev, current: 1 }));
                  }} />
                  <Input placeholder="粉丝数最大" type="number" style={{ width: 120 }} onChange={(e)=>{
                    const v = e?.target?.value;
                    setFilters(prev=>({ ...prev, maxFollowers: v ? Number(v) : undefined }));
                    setPagination(prev=>({ ...prev, current: 1 }));
                  }} />
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingAccount(null);
                      setFormSeed(Date.now());
                      setModalVisible(true);
                    }}
                  >
                    添加{isWhitePool ? '白号' : '账号'}
                  </Button>
                  <Button 
                    icon={<SettingOutlined />} 
                    onClick={() => setAccountTypeModalVisible(true)}
                  >
                    账号类型
                  </Button>
                  <Button onClick={() => setImportVisible(true)}>批量导入</Button>
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'excel', label: '导出Excel', onClick: handleExportExcel },
                        { key: 'csv', label: '导出CSV', onClick: handleExport }
                      ]
                    }}
                  >
                    <Button icon={<ExportOutlined />}>导出</Button>
                  </Dropdown>
                  <Button icon={<ReloadOutlined />} onClick={fetchAccounts}>刷新</Button>
                </Space>
              </Col>
            </Row>
          )}
        </div>

        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: '16px', padding: '8px', background: '#f0f0f0', borderRadius: '4px' }}>
            <Space wrap>
              <span>已选择 {selectedRowKeys.length} 个账号</span>
              {isWhitePool ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SwapOutlined />}
                    onClick={() => handleTransferIn(selectedRowKeys, 'pending')}
                  >
                    转入待售
                  </Button>
                  <Button
                    size="small"
                    icon={<SwapOutlined />}
                    onClick={() => handleTransferIn(selectedRowKeys, 'banned')}
                  >
                    转入起号
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    onClick={() => handleBatchStatusUpdate('banned')}
                  >
                    设为起号
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleBatchStatusUpdate('pending')}
                  >
                    设为待售
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleBatchStatusUpdate('suspended')}
                  >
                    设为封禁
                  </Button>
                </>
              )}
              <Button
                size="small"
                onClick={() => setSelectedRowKeys([])}
              >
                取消选择
              </Button>
              <Button
                size="small"
                danger
                onClick={handleBatchDelete}
              >
                批量删除
              </Button>
            </Space>
          </div>
        )}

        {isMobile ? (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {accounts.map(record => (
                <Card key={record.id} size="small" bodyStyle={{ padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{record.username}</div>
                      {record.email && (
                        <div style={{ fontSize: 12, color: '#666' }}>{record.email}</div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>
                        地区：{record.country || '未设置'}
                        <span style={{ margin: '0 8px' }} />
                        橱窗：{record.window_status || '未开'}
                        <span style={{ margin: '0 8px' }} />
                        粉丝/占比：{(record.followers || 0).toLocaleString()} / {record.region_ratio || '-'}
                      </div>
                      <div style={{ marginTop: 6 }}>
                        {getCategoryTag(record.category)}
                        <span style={{ margin: '0 8px' }} />
                        {getStatusTag(record.status)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#1890ff', fontWeight: 600 }}>¥{record.price}</div>
                      <div style={{ color: record.profit > 0 ? '#52c41a' : '#f5222d' }}>¥{record.profit}</div>
                    </div>
                  </div>
                  <Row gutter={8} style={{ marginTop: 12 }}>
                    <Col span={6}>
                      <Button
                        block
                        size="middle"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 36, padding: 0 }}
                        icon={<EyeOutlined />}
                        onClick={async () => {
                          setViewingAccount(record);
                          setDetailModalVisible(true);
                          setImagesLoading(true);
                          setViewingImages([]);
                          try {
                            const res = await getAccountImages(record.id);
                            const raws = Array.isArray(res.image_urls) ? res.image_urls : [];
                            setViewingImages(raws);
                          } catch (e) {
                            setViewingImages([]);
                          } finally {
                            setImagesLoading(false);
                          }
                        }}
                      >
                        详情
                      </Button>
                    </Col>
                    <Col span={6}>
                      <Button
                        block
                        size="middle"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 36, padding: 0 }}
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                      >
                        编辑
                      </Button>
                    </Col>
                    {isWhitePool ? (
                      <Col span={6}>
                        <Dropdown
                          menu={{
                            items: [
                              { key: 'pending', label: '转入待售', onClick: () => handleTransferIn([record.id], 'pending') },
                              { key: 'banned', label: '转入起号', onClick: () => handleTransferIn([record.id], 'banned') }
                            ]
                          }}
                        >
                          <Button
                            block
                            size="middle"
                            type="primary"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 36, padding: 0 }}
                            icon={<SwapOutlined />}
                          >
                            转入
                          </Button>
                        </Dropdown>
                      </Col>
                    ) : (
                      record.status !== 'sold' && (
                        <Col span={6}>
                          <Button
                            block
                            size="middle"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 36, padding: 0 }}
                            icon={<ShoppingCartOutlined />}
                            onClick={() => { setSelectedAccountForSale(record); setSaleModalVisible(true); }}
                          >
                            销售
                          </Button>
                        </Col>
                      )
                    )}
                    <Col span={6}>
                      <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                        <Button
                          block
                          size="middle"
                          danger
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 36, padding: 0 }}
                          icon={<DeleteOutlined />}
                        >
                          删除
                        </Button>
                      </Popconfirm>
                    </Col>
                  </Row>
                  <Row gutter={8} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <Button
                        block
                        size="middle"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyInfo(record)}
                      >
                        复制关键信息
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                onChange={(page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || prev.pageSize }));
                }}
              />
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={accounts}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || prev.pageSize
                }));
              }
            }}
            rowSelection={rowSelection}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>

      <Modal
        title={editingAccount ? '编辑账号' : (isWhitePool ? '添加白号' : '添加账号')}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={modalWidth || (isMobile ? '95%' : 1000)}
        destroyOnClose={true}
      >
        <AccountForm
          key={formSeed}
          account={editingAccount}
          onSuccess={handleModalSuccess}
          onCancel={handleModalClose}
          defaultStatus={isWhitePool ? 'white' : 'pending'}
        />
      </Modal>

      <Modal
        title="添加销售记录"
        open={saleModalVisible}
        onCancel={handleSaleModalClose}
        footer={null}
        width={modalWidth || (isMobile ? '95%' : 800)}
      >
        <SaleForm
          accountId={selectedAccountForSale?.id}
          onSuccess={handleSaleModalSuccess}
          onCancel={handleSaleModalClose}
        />
      </Modal>

      <Modal
        title="账号详情"
        open={detailModalVisible}
        onCancel={handleDetailModalClose}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={() => handleCopyInfo(viewingAccount)}>
            复制关键信息
          </Button>,
          <Button key="close" onClick={handleDetailModalClose}>
            关闭
          </Button>
        ]}
        width={modalWidth || (isMobile ? '95%' : 800)}
        bodyStyle={{ padding: isMobile ? 12 : 24 }}
      >
        {viewingAccount && (
          <div>
            <Descriptions
              bordered
              size={isMobile ? 'small' : 'default'}
              column={isMobile ? 1 : 2}
              labelStyle={{
                wordBreak: 'keep-all',
                whiteSpace: 'nowrap',
                width: isMobile ? 96 : undefined
              }}
              contentStyle={{
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              <Descriptions.Item label="用户名" span={2}>
                <strong>{viewingAccount.username}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">
                {viewingAccount.email || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">
                {viewingAccount.phone || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="账号地区">
                {viewingAccount.country || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="密码">
                {viewingAccount.password || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="邮箱密码">
                {viewingAccount.emailPassword || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="2FA">
                {viewingAccount.two_fa || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="API">
                {viewingAccount.api_key ? (
                  /^https?:\/\//i.test(String(viewingAccount.api_key).trim()) ? (
                    <a
                      href={String(viewingAccount.api_key).trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {viewingAccount.api_key}
                    </a>
                  ) : (
                    <span style={{ wordBreak: 'break-all' }}>{viewingAccount.api_key}</span>
                  )
                ) : (
                  '未设置'
                )}
              </Descriptions.Item>
              {(() => {
                if (imagesLoading || !viewingImages || viewingImages.length === 0) {
                  // 不占位：未加载或没有图片时不渲染该行
                  return null;
                }
                const imgs = viewingImages;
                return imgs && imgs.length > 0 ? (
                  <Descriptions.Item label="图片" span={isMobile ? 1 : 2}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, minHeight: isMobile ? 140 : 0 }}>
                      <Image.PreviewGroup>
                        {imgs.map((u, idx) => (
                          <div
                            key={`${u}-${idx}`}
                            style={{ width: isMobile ? '100%' : 'calc(50% - 6px)' }}
                          >
                            <Image
                              src={u && u.startsWith('data:') ? u : `${u}${u && u.includes('?') ? '&' : '?'}_=${Date.now()}`}
                              // 移动端不懒加载/不占位，PC 默认正常
                              loading={isMobile ? undefined : 'lazy'}
                              placeholder={isMobile ? undefined : (
                                <div style={{
                                  width: '100%',
                                  height: 140,
                                  background: '#f5f5f5',
                                  borderRadius: 6
                                }} />
                              )}
                              style={{ width: '100%', height: isMobile ? 120 : 140, objectFit: 'cover', borderRadius: 6 }}
                            />
                          </div>
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  </Descriptions.Item>
                ) : null;
              })()}
              <Descriptions.Item label="粉丝数">
                {(viewingAccount.followers || 0).toLocaleString()}
                {viewingAccount.region_ratio ? ` / ${viewingAccount.region_ratio}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="橱窗状态">
                {viewingAccount.window_status === '已开'
                  ? `已开${viewingAccount.window_opened_at ? '（' + new Date(viewingAccount.window_opened_at).toLocaleDateString() + '）' : ''}`
                  : (viewingAccount.window_status || '未开')}
              </Descriptions.Item>
              <Descriptions.Item label="分类">
                <Tag color="blue">{viewingAccount.category || '未分类'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={
                  viewingAccount.status === 'pending' ? 'orange' :
                  viewingAccount.status === 'sold' ? 'blue' :
                  viewingAccount.status === 'suspended' ? 'red' :
                  viewingAccount.status === 'banned' ? 'green' :
                  viewingAccount.status === 'white' ? 'default' : 'default'
                }>
                  {viewingAccount.status === 'pending' ? '待售' : 
                   viewingAccount.status === 'sold' ? '已售' : 
                   viewingAccount.status === 'suspended' ? '封禁' : 
                   viewingAccount.status === 'banned' ? '起号' :
                   viewingAccount.status === 'white' ? '白号' : viewingAccount.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="售价">
                <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                  ¥{viewingAccount.price}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="成本">
                ¥{viewingAccount.cost || 0}
              </Descriptions.Item>
              <Descriptions.Item label="利润">
                <span style={{ 
                  color: viewingAccount.profit > 0 ? '#52c41a' : '#f5222d',
                  fontWeight: 'bold'
                }}>
                  ¥{viewingAccount.profit || 0}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {viewingAccount.created_date ? new Date(viewingAccount.created_date).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最后检查时间">
                {viewingAccount.last_checked ? 
                  new Date(viewingAccount.last_checked).toLocaleString() : '未检查'}
              </Descriptions.Item>
              {viewingAccount.tags && (
                <Descriptions.Item label="标签" span={isMobile ? 1 : 2}>
                  {viewingAccount.tags.split(',').map((tag, index) => (
                    <Tag key={index} color="purple" style={{ marginBottom: 4 }}>{tag.trim()}</Tag>
                  ))}
                </Descriptions.Item>
              )}
              {viewingAccount.window_opened_at && (
                <Descriptions.Item label="开通橱窗日期">
                  {new Date(viewingAccount.window_opened_at).toLocaleDateString()}
                </Descriptions.Item>
              )}
              {viewingAccount.description && (
                <Descriptions.Item label="描述" span={isMobile ? 1 : 2}>
                  {viewingAccount.description}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>

      <Modal
        title={isWhitePool ? '批量导入白号' : '批量导入账号'}
        open={importVisible}
        onCancel={() => setImportVisible(false)}
        footer={null}
      >
        <p>
          {isWhitePool
            ? '导入后默认进入白号库存（状态=白号）。支持 CSV / Excel，表头建议：用户名,密码,邮箱,邮箱密码,2FA。'
            : '支持 CSV 文件或 Excel（第一行为列名）：用户名,密码,邮箱,邮箱密码,橱窗状态,粉丝,地区,类型,价格。'}
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input
            accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            type="file"
            onChange={(e)=>{
              const f = e?.target?.files?.[0];
              if (!f) return;
              const name = (f.name || '').toLowerCase();
              if (name.endsWith('.xlsx')) {
                handleImportExcel(f);
              } else {
                handleImportFile(f);
              }
            }}
            disabled={importing}
          />
          <Button onClick={downloadImportTemplate} size="small">下载模板</Button>
        </div>
        <p style={{ color: '#999' }}>
          {isWhitePool
            ? '登录完成后，在列表中点「转入」即可进入账号管理（待售/起号）。'
            : '注意：导入后不可撤销，请确认数据准确无误。'}
        </p>
      </Modal>

      {/* 账号类型管理模态框 */}
      <Modal
        title="账号类型管理"
        open={accountTypeModalVisible}
        onCancel={() => setAccountTypeModalVisible(false)}
        footer={null}
        width={600}
      >
        <AccountTypeManager 
          accountTypes={accountTypes}
          onRefresh={fetchAccountTypes}
        />
      </Modal>
    </div>
  );
};

export default AccountList;
